import type React from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';

type InsertPlaceholderMethod = (text: string) => void;

/**
 * A hook to insert text on cursor position inside textarea/input element.
 * @param inputRef - Ref of either textarea or input HTML element
 * @param [setFieldValue] - A callback to set the value. If not passed - just input's value will be set.
 * @returns a function that, when provided text value, will insert it into inputRef input at selection start.
 * The input will be then focused and the inserted text will be selected.
 */
export const useInputInsertText = (
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>,
  setFieldValue?: (newValue: string) => void,
): InsertPlaceholderMethod =>
  useCallbackRef((insertedText: string) => {
    const setValue =
      setFieldValue ||
      ((newValue: string) => {
        const inputElement = inputRef.current;
        if (inputElement) {
          inputElement.value = newValue;
        }
      });

    if (inputRef.current) {
      const insertionPos: number = inputRef.current.selectionStart || 0;
      const head = inputRef.current.value.slice(0, insertionPos);
      const tail = inputRef.current.value.slice(insertionPos);
      const valueWithPlaceholderInserted = `${head}${insertedText}${tail}`;
      setValue(valueWithPlaceholderInserted);

      // NOTE: Timeout used to restore selection position right after the state was set.
      window.setTimeout(() => {
        const inputElement = inputRef.current;
        if (inputElement) {
          inputElement.selectionStart = insertionPos;
          inputElement.selectionEnd = insertionPos + insertedText.length;
          inputElement.focus();
        }
      }, 0);
    }
  });
